import { useHover, useResponsive } from 'ahooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import ImageGallery, { ReactImageGalleryItem } from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'react-translate-plus';
import styled from 'styled-components';

import { Button, CircularButton, Icon, icons } from '@hoffmann/pattern-library-2';

import { mediaQuery } from '../../../app/setupResponsiveness';
import { withStyledClasses } from '../../../styled/withStyledClasses';
import { Thumbs } from './Thumbs';

const SLIDE_INTERVAL_MS = 3000;

export type CardInfo = {
  articleNumber: string;
  png: string;
  webp: string;
  nameLocalisation: string;
  actionText: string;
  actionText2: string;
};

interface Props {
  data?: CardInfo[];
}
export const LandingPageCard: React.FC<Props> = ({ data }) => {
  /* lib hooks */
  const ref = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<ImageGallery>(null);
  const isHovering = useHover(ref);
  const t = useTranslate('LandingPageV2');
  const history = useNavigate();
  const { xl } = useResponsive();

  /* local states */
  const [currentIdx, setCurrentIdx] = useState<number>(0);
  const [items, setItems] = useState<ReactImageGalleryItem[]>([]);
  const [animating, setAnimating] = useState<boolean>(false);

  /* local vars */
  const slider = sliderRef.current;
  const currentCard = data?.length && data[currentIdx];
  const length = data?.length || 0;

  useEffect(() => {
    setItems((data || []).map((item) => ({ original: item.png, srcSet: item.webp })));
    setCurrentIdx(0);
  }, [data]);

  useEffect(() => {
    if (!slider) {
      return;
    }
    if (isHovering) {
      slider.play();
    } else {
      slider.pause();
    }
  }, [isHovering, slider]);

  const handleThumbClick = useCallback(
    (index: number) => {
      if (!slider) {
        return;
      }
      setCurrentIdx(index);
      slider.pause();
      slider.slideToIndex(index);
      slider.play();
    },
    [slider],
  );

  const handleArrowClick = useCallback(
    (offset: number) => {
      if (!slider) {
        return;
      }
      setCurrentIdx((current) => {
        slider.pause();
        // slider.slideToIndex(slider.getCurrentIndex() + offset);
        (slider as any).slideTo(null, offset > 0 ? 'right' : 'left');
        slider.play();
        return (current + length + offset) % (length || 1);
      });
    },
    [slider, length],
  );

  const handleBeforeSlide = useCallback((idx: number) => {
    setAnimating(true);
    setCurrentIdx(idx);
  }, []);

  const handleSlide = useCallback((idx: number) => {
    setAnimating(false);
    setCurrentIdx(idx);
  }, []);

  return (
    <Styles isHovering={isHovering} ref={ref}>
      <SlideWrapper>
        {!xl && (
          <Arrows>
            <LeftArrow
              icon={<Icon source={icons.ArrowBack} />}
              onClick={() => handleArrowClick(-1)}
              disabled={!length || animating}
              size={200}
              ghost
            />
            <RightArrow
              icon={<Icon source={icons.ArrowForward} />}
              onClick={() => handleArrowClick(1)}
              disabled={!length || animating}
              size={200}
              ghost
            />
          </Arrows>
        )}
        <ImageGallery
          ref={sliderRef}
          items={items}
          disableKeyDown
          onSlide={handleSlide}
          onBeforeSlide={handleBeforeSlide}
          showBullets={false}
          showFullscreenButton={false}
          showPlayButton={false}
          slideInterval={SLIDE_INTERVAL_MS}
          showNav={xl}
          renderLeftNav={() =>
            isHovering && (
              <LeftArrow
                icon={<Icon source={icons.ArrowBack} />}
                onClick={() => handleArrowClick(-1)}
                disabled={!length || animating}
                size={200}
                ghost
              />
            )
          }
          renderRightNav={() =>
            isHovering && (
              <RightArrow
                icon={<Icon source={icons.ArrowForward} />}
                onClick={() => handleArrowClick(1)}
                disabled={!length || animating}
                size={200}
                ghost
              />
            )
          }
        />
      </SlideWrapper>
      <ActionsWrapper>
        <Action>
          <span>{currentCard?.actionText}</span>
          <span>
            <ActionLink href={`/assemble/${encodeURIComponent(currentCard.articleNumber)}`}>
              {currentCard?.nameLocalisation}
            </ActionLink>
          </span>
          <span>{currentCard.actionText2}</span>
        </Action>
        <ConfigureButton
          label={t('BUTTON_CONFIGURE')}
          onClick={() =>
            currentCard && history(`/assemble/${encodeURIComponent(currentCard.articleNumber)}`)
          }
          hoffmannTheme="brand"
        />
        <Thumbs selected={currentIdx} total={data.length} onClick={handleThumbClick} />
      </ActionsWrapper>
    </Styles>
  );
};

const Styles = styled.div<{ isHovering: boolean }>(
  ({ isHovering, theme: { colors } }) => `
  align-items: center;
  background-color: ${colors.layout30};
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  min-height: 450px;
  position: relative;
  border-bottom: 1px solid ${colors.customButtonBorderGrey};

  @media screen and (${mediaQuery.mdFrom}) {
    flex-flow: row wrap;
    flex: 0 1 100%;
    min-height: 360px;
  }

  @media screen and (${mediaQuery.xlFrom}) {
    background-color: ${isHovering ? colors.layout30 : colors.white};
    flex: 1 1 33%;
    flex-flow: column nowrap;
  }

`,
);

const Arrows = styled.div`
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

const Arrow = styled(withStyledClasses(CircularButton))(
  ({ theme: { colors } }) => `
  color: ${colors.customBlueGrey1};
  font-size: 3rem;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;

  &:hover,
  &.hpl2-CircularButton--ghost:disabled {
    color: ${colors.customBlueGrey1};
  }

`,
);

const LeftArrow = styled(Arrow)`
  left: 0.5rem;
`;

const RightArrow = styled(Arrow)`
  right: 0.5rem;
`;

const SlideWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-flow: column nowrap;
  flex: 1;
  height: 100%;
  justify-content: center;
  position: relative;
  width: 100%;

  @media screen and (${mediaQuery.mdFrom}) {
    flex: 0 0 50%;
  }

  @media screen and (${mediaQuery.xlFrom}) {
    margin-top: 1rem;
    flex: 0 0 60%;
    width: 100%;
  }

  .image-gallery {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
  .image-gallery-content,
  .image-gallery-slide-wrapper,
  .image-gallery-swipe,
  .image-gallery-slides,
  .image-gallery-slide {
    height: 100%;
  }

  .image-gallery-slide {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    .image-gallery-image {
      height: 100%;
      max-height: 100%;
      padding: 1rem 2.5rem;

      @media screen and (${mediaQuery.xlFrom}) {
        padding: 0 2.5rem;
      }
    }
  }
`;

const ActionsWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-flow: column nowrap;
  gap: 1rem;
  justify-content: center;
  flex: 0 0 176px;

  @media screen and (${mediaQuery.mdFrom}) {
    flex: 1;
    margin-top: 1rem;
    margin-bottom: 1rem;
    gap: 2rem;
    width: 100%;
  }

  @media screen and (${mediaQuery.lgFrom}) {
    min-height: 200px;
  }
`;

const ConfigureButton = styled(withStyledClasses(Button))`
  &.hpl2-Button {
    display: block;
    min-width: 240px;
    height: 48px;
    text-transform: none;
    letter-spacing: normal;
    width: unset;
    width: min-content;
  }
`;

const Action = styled.div(
  ({ theme: { colors } }) => `
  font-size: 24px;
  color: ${colors.layout100};
  display: block;
  text-align: center;

  @media screen and (${mediaQuery.mdFrom}) {
    margin-top: 1rem;
    padding: 1rem;
    width: 100%;
  }

  > span + span {
    margin-left: .5ch;
  }
`,
);

const ActionLink = styled.a(
  ({ theme: { colors } }) => `
  color: ${colors.brand100};
  text-decoration: underline;

  &:hover {
    color: ${colors.brand100};
    text-decoration: underline;
  }
`,
);
